import { Home, Users, FileText, Settings, LogOut, CreditCard, Package, Receipt, BookOpen, Newspaper, Sliders, Bot, Zap, AppWindow, FileStack, Table2, Mail } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logo from "@/assets/logo_header.png";

const mainItems = [
  { title: "Início", url: "/admin", icon: Home },
];

const cadastrosItems = [
  { title: "Profissionais", url: "/admin/cadastros/profissionais", icon: Users },
  { title: "Pacientes", url: "/admin/cadastros/pacientes", icon: Users },
];

const siteItems = [
  { title: "Cases", url: "/admin/site/cases", icon: BookOpen },
  { title: "Blog", url: "/admin/site/blog", icon: Newspaper },
];

const financeiroItems = [
  { title: "Assinaturas", url: "/admin/assinaturas", icon: CreditCard },
  { title: "Planos", url: "/admin/planos", icon: Package },
  { title: "Tabelas de Preço", url: "/admin/tabelas-preco", icon: Table2 },
  { title: "Pagamentos", url: "/admin/pagamentos", icon: Receipt },
];

const configItems = [
  { title: "Gerais", url: "/admin/configuracoes/gerais", icon: Settings },
  { title: "IAs", url: "/admin/configuracoes/ias", icon: Bot },
  { title: "Pagar.me", url: "/admin/configuracoes/pagarme", icon: Zap },
  { title: "Usuários", url: "/admin/configuracoes/usuarios", icon: Users },
  { title: "Aplicativos", url: "/admin/configuracoes/aplicativos", icon: AppWindow },
  { title: "E-mails", url: "/admin/configuracoes/emails", icon: Mail },
  { title: "Templates", url: "/admin/templates", icon: FileStack },
];

export function AdminSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [alertsCount, setAlertsCount] = useState(0);

  useEffect(() => {
    loadUserProfile();
    loadAlertCount();
  }, []);

  const loadUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profileData } = await supabase
        .from("profiles")
        .select("avatar_url, full_name")
        .eq("id", user.id)
        .single();

      if (profileData) {
        setUserName(profileData.full_name || "Admin");
        setAvatarUrl(profileData.avatar_url);
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    }
  };

  const loadAlertCount = async () => {
    try {
      const { count } = await supabase
        .from("documents_onboarding")
        .select("*", { count: "exact", head: true })
        .eq("status", "erro");

      setAlertsCount(count || 0);
    } catch (error) {
      console.error("Error loading alerts:", error);
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const getNavCls = ({ isActive }: { isActive: boolean }) =>
    isActive ? "bg-primary/10 text-primary font-medium border-l-2 border-primary" : "hover:bg-muted/50";

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Logout realizado com sucesso");
      navigate("/auth");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Erro ao fazer logout");
    }
  };

  return (
    <Sidebar className={state === "collapsed" ? "w-14" : "w-60"} collapsible="icon">
      <SidebarContent>
        {/* Logo and User Info */}
        <div className="p-4 border-b space-y-4">
          <img src={logo} alt="Elinia" className="h-auto w-auto" />

          {state !== "collapsed" && (
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} alt={userName} />
                ) : (
                  <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-white">
                    {userName.substring(0, 2).toUpperCase() || "AD"}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1 overflow-hidden">
                <p className="text-sm font-medium truncate">{userName}</p>
                <p className="text-xs text-muted-foreground">Administrador</p>
              </div>
            </div>
          )}
        </div>
        <SidebarGroup>
          <SidebarGroupLabel>Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} end className={getNavCls}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {state !== "collapsed" && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Cadastros</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {cadastrosItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {state !== "collapsed" && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Site</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {siteItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {state !== "collapsed" && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Financeiro</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {financeiroItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {state !== "collapsed" && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Configurações</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {configItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {state !== "collapsed" && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter className="border-t p-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} className="hover:bg-destructive/10 hover:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              {state !== "collapsed" && <span>Sair</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {state !== "collapsed" && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            v1.0.0
          </p>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}